<template>
  <VModal v-model="isVisible" :modal-title="title" :click-to-close="!loading && clickToClose" :esc-to-close="!loading && escToClose" @opened="opened" :modal-class="modalClass" :content-class="contentClass" :hide-close-button="loading" :is-nested-modal="isNestedModal" :prevent-close-nested="preventCloseNested" :z-index="zIndex" :id="id" size="dialog" :dynamic-width="dynamicWidth" @closed="emit('closed')">
    <!-- Header icon slot -->
    <template #header-icon>
      <span v-if="dialogIcon" class="material-symbols-outlined text-xl" :class="iconColorClass">{{ dialogIcon }}</span>
      <slot v-if="customIcon" name="icon"></slot>
    </template>

    <!-- Main content -->
    <div class="text-left">
      <!-- Mesaj prop'u ile gelen standart mesaj -->
      <p v-if="message && !useContentSlot" class="text-base text-gray-600 break-words">{{ message }}</p>

      <!-- useContentSlot true ise veya default slot içerik varsa bunu kullan -->
      <slot v-if="useContentSlot || $slots.default"></slot>

      <!-- Doğrulama checkbox'ı -->
      <div v-if="requireConfirmation" class="mt-4 text-left">
        <label class="flex items-center cursor-pointer">
          <input type="checkbox" v-model="confirmChecked" class="rounded border-gray-300 text-primary shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50 mr-2"/>
          <span class="text-sm text-gray-700">{{ confirmationText }}</span> 
        </label>
      </div>
    </div>

    <!-- Footer (Butonlar) -->
    <template #footer>
      <div class="flex justify-end space-x-2">
        <slot name="footer-buttons">
          <custom-button v-if="showCancelButton" @click="handleDecline" :button-text="cancelButtonText" :color="cancelButtonColor || typeConfig.cancelColor" variant="outline" :size="buttonSize" :is-loading="loading && cancelButtonLoading" :is-disabled="loading"/>
          <custom-button @click="handleConfirm" :button-text="confirmButtonText" :color="confirmButtonColor || typeConfig.confirmColor" :size="buttonSize" :is-loading="loading && !cancelButtonLoading" :is-disabled="loading || (requireConfirmation && !confirmChecked)" :show-icon="showConfirmButtonIcon || typeConfig.showIcon" :icon="confirmButtonIcon || typeConfig.icon"/>
        </slot>
      </div>
    </template>
  </VModal>
</template>

<script setup>
import { ref, watch, computed, reactive } from 'vue'
import VModal from '../../modals/VModal.vue'
import CustomButton from '@/components/base/buttons/VButton.vue'

const props = defineProps({
  // Temel özellikler
  modelValue: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: '',
  },
  message: {
    type: String,
    default: '',
  },
  // Yeni prop: İçerik slot'u kullanımını kontrol eder
  useContentSlot: {
    type: Boolean,
    default: false,
  },
  // Diyalog tipleri ve görünüm
  type: {
    type: String,
    default: 'delete', // 'delete', 'info', 'warning', 'success', 'error'
    validator: (value) => ['delete', 'info', 'warning', 'success', 'error'].includes(value),
  },
  // Eski dialogType prop'unu desteklemek için (geriye dönük uyumluluk)
  dialogType: {
    type: String,
    default: '',
    validator: (value) => ['', 'delete', 'info', 'warning', 'success', 'error'].includes(value),
  },
  // Icon ve görselleştirme 
  icon: {
    type: String,
    default: '',
  },
  iconClass: {
    type: String,
    default: '',
  },
  defaultIcon: {
    type: Boolean,
    default: true,
  },
  customIcon: {
    type: Boolean,
    default: false,
  },
  // Doğrulama seçenekleri
  requireConfirmation: {
    type: Boolean,
    default: false,
  },
  confirmationText: {
    type: String,
    default: 'Bu işlemi yapmak istediğimi onaylıyorum',
  },
  // Buton özellikleri
  confirmButtonText: {
    type: String,
    default: 'Evet',
  },
  cancelButtonText: {
    type: String,
    default: 'Hayır',
  },
  confirmButtonColor: {
    type: String,
    default: 'primary',
  },
  cancelButtonColor: {
    type: String,
    default: 'secondary',
  },
  showCancelButton: {
    type: Boolean,
    default: false,
  },
  buttonSize: {
    type: String,
    default: 'md',
  },
  showConfirmButtonIcon: {
    type: Boolean,
    default: false,
  },
  confirmButtonIcon: {
    type: String,
    default: '',
  },
  cancelButtonLoading: {
    type: Boolean,
    default: false,
  },
  // Yükleme durumu
  loading: {
    type: Boolean,
    default: false,
  },
  // Modal kapanma kontrolü
  clickToClose: {
    type: Boolean,
    default: true,
  },
  escToClose: {
    type: Boolean,
    default: true,
  },
  autoClose: {
    type: Boolean,
    default: true,
  },
  // Modal stil özelleştirme
  modalClass: {
    type: String,
    default: 'flex justify-center items-center z-50',
  },
  contentClass: {
    type: String,
    default: 'flex flex-col bg-white border rounded-lg space-y-2 shadow-xl relative',
  },
  // Başlık uzunluğuna göre genişliği ayarla
  dynamicWidth: {
    type: Boolean,
    default: true,  // Varsayılan olarak true yaparak tüm diyalogların uzun başlıklarda otomatik genişlemesini sağlıyoruz
  },
  // İç içe modal özellikleri
  isNestedModal: {
    type: Boolean,
    default: false,
  },
  preventCloseNested: {
    type: Boolean,
    default: false,
  },
  // Z-index kontrolü
  zIndex: {
    type: [Number, String],
    default: null,
  },
  // Modal tanımlayıcısı
  id: {
    type: String,
    default: null,
  },
})

const emit = defineEmits(['opened', 'closed', 'update:modelValue', 'update:show', 'confirm', 'decline'])

// Reactive states
const isVisible = ref(props.modelValue)
const confirmChecked = ref(false)

// Dialog type configurations
const dialogTypes = reactive({
  delete: {
    icon: 'delete_forever', // Çöp kutusu ikonu
    iconColor: 'text-red-600', // Kırmızı renk
    confirmColor: 'danger',
    cancelColor: 'secondary',
    showIcon: true,
  },
  info: {
    icon: 'info',
    iconColor: 'text-blue-600',
    confirmColor: 'primary',
    cancelColor: 'secondary',
    showIcon: false,
  },
  warning: {
    icon: 'warning',
    iconColor: 'text-amber-500',
    confirmColor: 'warning',
    cancelColor: 'secondary',
    showIcon: false,
  },
  success: {
    icon: 'check_circle',
    iconColor: 'text-green-600',
    confirmColor: 'success',
    cancelColor: 'secondary',
    showIcon: true,
  },
  error: {
    icon: 'error',
    iconColor: 'text-red-600',
    confirmColor: 'danger',
    cancelColor: 'secondary',
    showIcon: false,
  },
})

// Determine the actual type to use (prioritize 'type' prop, fallback to 'dialogType' for backward compatibility)
const activeType = computed(() => {
  return props.type || props.dialogType || 'info'
})

// Get the type configuration based on the active type
const typeConfig = computed(() => {
  return dialogTypes[activeType.value] || dialogTypes.info
})

// Determine the icon to use: custom icon > prop icon > type-based icon
const dialogIcon = computed(() => {
  if (props.customIcon) return null
  if (!props.defaultIcon) return null

  return props.icon || typeConfig.value.icon
})

// Determine the icon color class: custom class > type-based class
const iconColorClass = computed(() => {
  return props.iconClass || typeConfig.value.iconColor
})

// Watch for changes in modelValue prop
watch(() => props.modelValue, (newValue) => {
  isVisible.value = newValue
})

// Watch for changes in isVisible and emit update
watch(isVisible, (newValue) => {
  emit('update:modelValue', newValue)
  // For backward compatibility with components using update:show
  emit('update:show', newValue)
})

const opened = () => emit('opened')

const closeModal = () => {
  if (!props.loading) {
    isVisible.value = false
  }
}

const handleConfirm = () => {
  if (!props.loading) {
    // Confirmation checkbox validation
    if (props.requireConfirmation && !confirmChecked.value) {
      // Do nothing - button will be disabled
      return
    } else {
      emit('confirm')
      // Only auto-close if the autoClose prop is true
      if (props.autoClose) {
        closeModal()
      }
    }
  }
}

const handleDecline = () => {
  if (!props.loading) {
    emit('decline')
    // Always close on decline
    closeModal()
  }
}
</script>

<style>
/* VDialog-specific styles if needed */
</style>
